import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SalesConsultModal from '../../components/feature/SalesConsultModal';
import { useSEO } from '../../utils/seo';

export default function PricingPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  useSEO({
    title: '价格方案 - 智研云 | 基础版 | 专业版 | 企业版 | 灵活定价',
    description: '智研云提供基础版、专业版、企业版三种价格方案，满足不同规模企业需求。支持月付、季付、年付多种付费方式，年付享8折优惠。提供免费试用，企业定制方案可联系咨询。',
    keywords: '价格方案,基础版,专业版,企业版,定价,免费试用,企业定制',
    canonical: '/pricing',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: '智研云',
      description: '专业的研发费用管理系统',
      brand: {
        '@type': 'Brand',
        name: '智研云'
      },
      offers: [
        {
          '@type': 'Offer',
          name: '成长套餐',
          price: '3980',
          priceCurrency: 'CNY',
          priceValidUntil: '2025-12-31',
          availability: 'https://schema.org/InStock',
          description: '研发费200万/年以下，满足基本研发费用管理需求'
        },
        {
          '@type': 'Offer',
          name: '基础套餐',
          price: '9980',
          priceCurrency: 'CNY',
          priceValidUntil: '2025-12-31',
          availability: 'https://schema.org/InStock',
          description: '研发费201万-500万/年之间，全面的研发费用管理解决方案'
        },
        {
          '@type': 'Offer',
          name: '旗舰套餐',
          price: '19980',
          priceCurrency: 'CNY',
          priceValidUntil: '2025-12-31',
          availability: 'https://schema.org/InStock',
          description: '研发费501万-1000万/年之间，企业级定制化管理平台'
        }
      ]
    }
  });

  const [billingCycle, setBillingCycle] = useState<'yearly' | 'twoYears' | 'threeYears'>('yearly');

  const plans = [
    {
      name: '成长套餐',
      subtitle: '研发费200万/年以下',
      yearlyPrice: 3980,
      twoYearPrice: 7008,
      threeYearPrice: 7860,
      description: '满足基本研发费用管理需求',
      features: [
        '支持3个研发项目',
        '最多20名研发人员',
        '基础工时管理',
        '标准报表生成',
        '5GB云存储空间',
        '邮件技术支持',
        '数据保留1年'
      ],
      popular: false,
      color: 'from-gray-600 to-gray-700'
    },
    {
      name: '基础套餐',
      subtitle: '研发费201万-500万/年之间',
      yearlyPrice: 9980,
      twoYearPrice: 17568,
      threeYearPrice: 19560,
      description: '全面的研发费用管理解决方案',
      features: [
        '支持10个研发项目',
        '最多100名研发人员',
        '高级工时管理',
        '多口径报表生成',
        '50GB云存储空间',
        '风险预警功能',
        '在线客服支持',
        '数据保留3年',
        '移动端APP'
      ],
      popular: true,
      color: 'from-teal-600 to-blue-600'
    },
    {
      name: '旗舰套餐',
      subtitle: '研发费501万-1000万/年之间',
      yearlyPrice: 19980,
      twoYearPrice: 35168,
      threeYearPrice: 38960,
      description: '企业级定制化管理平台',
      features: [
        '无限研发项目',
        '无限研发人员',
        '智能工时分配',
        '全口径报表定制',
        '500GB云存储空间',
        '全方位风险管控',
        '专属客户经理',
        '数据永久保留',
        '多端同步使用',
        'API接口对接',
        '定制化开发',
        '现场培训服务'
      ],
      popular: false,
      color: 'from-purple-600 to-pink-600'
    }
  ];

  const getPriceByBillingCycle = (plan: typeof plans[0]) => {
    switch (billingCycle) {
      case 'yearly':
        return plan.yearlyPrice;
      case 'twoYears':
        return plan.twoYearPrice;
      case 'threeYears':
        return plan.threeYearPrice;
      default:
        return plan.yearlyPrice;
    }
  };

  const getBillingText = () => {
    switch (billingCycle) {
      case 'yearly':
        return '年';
      case 'twoYears':
        return '两年';
      case 'threeYears':
        return '三年';
      default:
        return '年';
    }
  };

  const getDiscountText = () => {
    switch (billingCycle) {
      case 'twoYears':
        return '约八八折';
      case 'threeYears':
        return '约六五折';
      default:
        return '';
    }
  };

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-indigo-50 via-purple-50 to-white py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Pricing%20and%20value%20concept%20with%20price%20tags%20and%20financial%20planning%20elements%20in%20soft%20indigo%20and%20purple%20gradient%20modern%20professional%20design&width=1920&height=600&seq=pricing-hero-bg&orientation=landscape')] opacity-5 bg-cover bg-center"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 sm:px-5 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                <i className="ri-price-tag-3-fill text-sm sm:text-base"></i>
                <span>灵活的价格方案</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                选择适合您的<br />
                <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">价格方案</span>
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-gray-600 mb-6 sm:mb-8 lg:mb-10 leading-relaxed px-2">
                无论企业规模大小，我们都有合适的方案<br className="hidden sm:block" />
                多年付费享受更多优惠，7天免费试用
              </p>

              {/* Billing Toggle */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-2xl mx-auto px-2">
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`flex-1 sm:flex-none px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl sm:rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base shadow-md ${
                    billingCycle === 'yearly'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:text-gray-900 hover:shadow-lg'
                  }`}
                >
                  按年付费
                </button>
                <button
                  onClick={() => setBillingCycle('twoYears')}
                  className={`flex-1 sm:flex-none px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl sm:rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer relative text-sm sm:text-base shadow-md ${
                    billingCycle === 'twoYears'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:text-gray-900 hover:shadow-lg'
                  }`}
                >
                  按两年付费
                  <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full shadow-md">
                    八八折
                  </span>
                </button>
                <button
                  onClick={() => setBillingCycle('threeYears')}
                  className={`flex-1 sm:flex-none px-6 sm:px-8 py-3.5 sm:py-4 rounded-xl sm:rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer relative text-sm sm:text-base shadow-md ${
                    billingCycle === 'threeYears'
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg scale-105'
                      : 'bg-white text-gray-700 hover:text-gray-900 hover:shadow-lg'
                  }`}
                >
                  按三年付费
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full shadow-md">
                    六五折
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-6 xl:gap-8">
              {plans.map((plan, index) => (
                <div
                  key={index}
                  className={`relative bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border-2 transition-all hover:shadow-2xl hover:-translate-y-2 ${
                    plan.popular
                      ? 'border-teal-500 shadow-xl lg:scale-105'
                      : 'border-gray-200 hover:border-teal-300'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
                        最受欢迎
                      </div>
                    </div>
                  )}

                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 px-2">{plan.subtitle}</p>
                    
                    <div className="mb-3 sm:mb-4">
                      <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
                        ¥{getPriceByBillingCycle(plan).toLocaleString()}
                      </span>
                      <span className="text-sm sm:text-base text-gray-600 ml-2">
                        /{getBillingText()}
                      </span>
                    </div>

                    {getDiscountText() && (
                      <p className="text-xs sm:text-sm text-teal-600 font-medium">
                        {getDiscountText()}优惠
                      </p>
                    )}

                    <p className="text-sm sm:text-base text-gray-600 mt-3 sm:mt-4 px-2">{plan.description}</p>
                  </div>

                  <button className={`w-full bg-gradient-to-r ${plan.color} text-white py-3 sm:py-4 rounded-xl sm:rounded-full font-semibold hover:shadow-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer mb-6 sm:mb-8 text-sm sm:text-base`}
                    onClick={() => setIsModalOpen(true)}
                  >
                    {plan.popular ? '立即购买' : '选择方案'}
                  </button>

                  <div className="space-y-3 sm:space-y-4">
                    <div className="font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-base sm:text-lg"></i>
                      <span>功能清单</span>
                    </div>
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 sm:gap-3">
                        <i className="ri-check-line text-teal-600 text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                        <span className="text-sm sm:text-base text-gray-700 leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Enterprise Contact */}
            <div className="mt-12 sm:mt-16 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl sm:rounded-3xl p-8 sm:p-10 lg:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Enterprise%20business%20concept%20with%20corporate%20buildings%20and%20professional%20team%20in%20dark%20elegant%20background%20modern%20luxury%20design&width=1920&height=400&seq=pricing-enterprise-bg&orientation=landscape')] opacity-10 bg-cover bg-center"></div>
              
              <div className="relative z-10">
                <h3 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">需要定制化方案？</h3>
                <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 px-2 max-w-3xl mx-auto">
                  我们为大型企业和集团客户提供专属定制服务<br className="hidden sm:block" />
                  包括私有化部署、定制开发、专属培训等
                </p>
                <button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-white text-gray-900 px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-full font-semibold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
                >
                  <i className="ri-customer-service-2-line mr-2"></i>
                  联系销售团队
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-10 sm:mb-12 lg:mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
                价格相关<span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">常见问题</span>
              </h2>
              <p className="text-sm sm:text-base text-gray-600 px-2">快速了解价格方案的常见疑问</p>
            </div>

            <div className="space-y-4 sm:space-y-5 lg:space-y-6">
              {[
                {
                  question: '可以免费试用吗？',
                  answer: '是的，我们提供7天免费试用，无需信用卡，试用期内可以体验所有专业版功能。'
                },
                {
                  question: '如何付款？',
                  answer: '我们支持对公转账、支付宝、微信支付等多种付款方式，企业客户可以申请月结。'
                },
                {
                  question: '可以随时升级或降级吗？',
                  answer: '可以的，您可以随时升级到更高版本，降级会在当前订阅周期结束后生效。'
                },
                {
                  question: '是否有退款政策？',
                  answer: '如果您在购买后7天内不满意，我们提供全额退款保证。'
                },
                {
                  question: '超出使用限制怎么办？',
                  answer: '系统会提前提醒您，您可以选择升级套餐或购买额外的资源包。'
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-7 shadow-md hover:shadow-lg transition-all border border-gray-100">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 flex items-start gap-2 sm:gap-3">
                    <i className="ri-question-line text-teal-600 text-xl sm:text-2xl flex-shrink-0 mt-0.5"></i>
                    <span className="leading-relaxed">{faq.question}</span>
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed pl-7 sm:pl-9 lg:pl-11">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-teal-600 to-blue-600 relative overflow-hidden">
          {/* 移除背景装饰 */}
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center relative z-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-6 sm:mb-8">
              立即开始免费试用
            </h2>
            <button 
              onClick={() => window.open('http://112.74.46.225:8002/admin/login?register=register', '_blank')}
              className="group bg-white text-teal-700 px-6 sm:px-8 lg:px-12 py-3 sm:py-4 lg:py-5 rounded-xl sm:rounded-2xl text-base sm:text-lg font-bold hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap inline-flex items-center"
            >
              <i className="ri-rocket-line mr-2 sm:mr-3 text-lg sm:text-xl group-hover:rotate-45 transition-transform duration-500"></i>
              开始免费试用
              <i className="ri-arrow-right-line ml-2 sm:mr-3 group-hover:translate-x-2 transition-transform duration-300"></i>
            </button>
          </div>
        </section>
      </main>
      <Footer />
      <SalesConsultModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
